import React, { useEffect, useState } from 'react';
import { fetchAppointmentsBySubmission, updateAppointmentRecord, fetchAllAppointments } from '../services/appointments.service';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchSlots, createSlot, deleteSlot, updateSlot } from '../services/slots.service';
import { useAuth } from '../context/AuthContext';
import { LogOut, Clock, Menu } from 'lucide-react';
import { Link } from 'react-router-dom';

const localizer = momentLocalizer(moment);

const AppointmentManager = ({ submissionId }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const loadAppointments = async () => {
    setLoading(true);
    const items = await fetchAllAppointments();
    setAppointments(items);
    setLoading(false);
  };

  const handleStatusChange = async (id, status) => {
    await updateAppointmentRecord({ id, status });
    loadAppointments();
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  const groupAppointmentsByDate = (appointments) => {
    return appointments.reduce((acc, appt) => {
      const date = appt.slot?.date;
      if (!date) return acc;
      if (!acc[date]) acc[date] = [];
      acc[date].push(appt);
      return acc;
    }, {});
  };  

  useEffect(() => {
     loadAppointments();
  }, [submissionId]);

  
  return (
    <div className="flex h-screen bg-gray-100">
    {/* Sidebar */}
    <div className={`bg-gray-900 text-white flex flex-col justify-between transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
      <div>
        <div className="p-4 flex items-center justify-between">
          <h1 className="text-xl font-bold whitespace-nowrap overflow-hidden transition-all duration-300">
            {sidebarOpen ? 'Submission Hub' : 'SH'}
          </h1>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
            <Menu size={20} />
          </button>
        </div>
        <nav className="mt-4">
          <ul>
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/manage-slots" className="flex items-center w-full text-white">
                <Clock size={20} />
                {sidebarOpen && <span className="ml-3">Manage Slots</span>}
              </Link>
            </li>
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/manage-appointments" className="flex items-center w-full text-white">
                <Clock size={20} />
                {sidebarOpen && <span className="ml-3">Manage Appointments</span>}
              </Link>
            </li>            
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/" className="flex items-center w-full text-white">
                <Clock size={20} />
                {sidebarOpen && <span className="ml-3">Manage Submissions</span>}
              </Link>
            </li>
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/message-templates" className="flex items-center w-full text-white">
                <Clock size={20} />
                {sidebarOpen && <span className="ml-3">Manage Message Templates</span>}
              </Link>
            </li>            
          </ul>
        </nav>
      </div>
      <div className="p-4">
        <div className="flex items-center cursor-pointer hover:bg-gray-800 p-2 rounded" onClick={handleLogout}>
          <LogOut size={20} />
          {sidebarOpen && <span className="ml-3">Logout</span>}
        </div>
      </div>
    </div>
    {/* Main content */}
    <div className="flex-1 flex flex-col overflow-hidden">
      <header className="bg-white shadow-sm px-4 md:px-6 py-4 flex items-center justify-end">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
            {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="ml-2">
            <p className="text-sm font-medium text-gray-700">{currentUser?.email || 'User'}</p>
            <p className="text-xs text-gray-500">Admin</p>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50">
        <div className="w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Manage Appointments</h2>

        <div>
          <h3 className="text-lg font-semibold mb-2">Appointments</h3>
          {loading ? (
            <p>Loading...</p>
          ) : appointments.length === 0 ? (
            <p>No appointments yet.</p>
          ) : (
            <ul className="space-y-3">
              {Object.entries(groupAppointmentsByDate(appointments)).map(([date, dayAppts]) => (
                <div key={date}>
                  <h4 className="text-md font-semibold mt-6 mb-2">{moment(date).format('dddd, MMMM D')}</h4>
                  <ul className="space-y-2">
                    {dayAppts.map((appt) => (
                      <li key={appt.id} className="p-3 border rounded shadow-sm bg-white">
                        <div className="text-sm">
                          <p><strong>Submission ID:</strong> {appt.submission?.publicId}</p>
                          <p><strong>Time:</strong> {appt.slot?.startTime} – {appt.slot?.endTime}</p>
                          <p><strong>Status:</strong> {appt.status}</p>
                        </div>
                        <div className="mt-2">
                          <select
                            value={appt.status}
                            onChange={(e) => handleStatusChange(appt.id, e.target.value)}
                            className="border p-1 rounded"
                          >
                            <option value="SCHEDULED">Scheduled</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="CANCELED">Canceled</option>
                            <option value="NO_SHOW">No Show</option>
                          </select>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </ul>
          )}
       </div>
       </div>
      </main>
    </div>
  </div>
  );
};

export default AppointmentManager;
